import {
  Component,
  Inject,
  OnInit,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

@Component({
  selector: 'app-face-compare-face-comparison-slider',
  imports: [CommonModule, FormsModule],
  templateUrl: './face-compare-face-comparison-slider.component.html',
  styleUrl: './face-compare-face-comparison-slider.component.css',
})
export class FaceCompareFaceComparisonSliderComponent implements OnInit {
  selectedImages: any[] = [];
  selectedFaces: any[] = [];
  faceImages: { [key: string]: string } = {};

  @ViewChild('image1') image1!: ElementRef<HTMLImageElement>;
  @ViewChild('image2') image2!: ElementRef<HTMLImageElement>;

  splitPosition = 50;

  image1Zoom = 1.1;
  image2Zoom = 1.1;
  image1PanX = 0;
  image1PanY = 0;
  image2PanX = 0;
  image2PanY = 0;

  private isPanning = false;
  private panStartX = 0;
  private panStartY = 0;
  private currentImage = 0;

  private isSplitDragging = false;

  constructor(
    public dialogRef: MatDialogRef<FaceCompareFaceComparisonSliderComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    this.selectedImages = data.selectedImages || [];
    this.selectedFaces = data.selectedFaces || [];
  }

  ngOnInit(): void {
    this.extractFaceImages();
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  getFaceImage(faceIndex: number): string {
    const faceId = `face_${faceIndex}`;
    return this.faceImages[faceId] || '';
  }

  startPan(event: MouseEvent, imageNumber: number): void {
    if (event.button === 0 && !this.isSplitDragging) {
      this.isPanning = true;
      this.currentImage = imageNumber;
      this.panStartX = event.clientX;
      this.panStartY = event.clientY;
      event.preventDefault();
      event.stopPropagation();
    }
  }

  onPan(event: MouseEvent, imageNumber: number): void {
    if (
      this.isPanning &&
      this.currentImage === imageNumber &&
      !this.isSplitDragging
    ) {
      const deltaX = event.clientX - this.panStartX;
      const deltaY = event.clientY - this.panStartY;

      if (imageNumber === 1) {
        this.image1PanX += deltaX;
        this.image1PanY += deltaY;
        this.constrainPan(1);
      } else {
        this.image2PanX += deltaX;
        this.image2PanY += deltaY;
        this.constrainPan(2);
      }

      this.panStartX = event.clientX;
      this.panStartY = event.clientY;
      event.preventDefault();
      event.stopPropagation();
    }
  }

  endPan(imageNumber: number): void {
    if (this.isPanning && this.currentImage === imageNumber) {
      this.isPanning = false;
      this.currentImage = 0;
    }
  }

  startSplitDrag(event: MouseEvent): void {
    this.isSplitDragging = true;
    this.isPanning = false;
    event.preventDefault();
    event.stopPropagation();
  }

  onSplitDrag(event: MouseEvent): void {
    if (this.isSplitDragging) {
      const container = document.querySelector(
        '.comparison-area',
      ) as HTMLElement;
      if (container) {
        const rect = container.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const percentage = (x / rect.width) * 100;
        this.splitPosition = Math.max(10, Math.min(90, percentage));
      }
      event.preventDefault();
    }
  }

  endSplitDrag(): void {
    this.isSplitDragging = false;
  }

  onZoomChange(imageNumber: number, event: Event): void {
    const target = event.target as HTMLInputElement;
    const zoom = parseFloat(target.value);

    if (imageNumber === 1) {
      this.image1Zoom = zoom;
      if (zoom <= 1) {
        this.image1PanX = 0;
        this.image1PanY = 0;
      } else {
        this.constrainPan(1);
      }
    } else {
      this.image2Zoom = zoom;
      if (zoom <= 1) {
        this.image2PanX = 0;
        this.image2PanY = 0;
      } else {
        this.constrainPan(2);
      }
    }
  }

  onWheel(event: WheelEvent, imageNumber: number): void {
    event.preventDefault();
    const currentZoom = imageNumber === 1 ? this.image1Zoom : this.image2Zoom;
    const newZoom = this.calculateNewZoom(currentZoom, event.deltaY, 0.1);
    this.updateImageZoomLevel(imageNumber, newZoom);
  }

  private calculateNewZoom(
    currentZoom: number,
    deltaY: number,
    zoomStep: number,
  ): number {
    let newZoom: number;

    if (deltaY < 0) {
      newZoom = Math.min(10, currentZoom + zoomStep);
    } else {
      newZoom = Math.max(0.5, currentZoom - zoomStep);
    }

    return Math.round(newZoom * 10) / 10;
  }

  private updateImageZoomLevel(imageNumber: number, newZoom: number): void {
    if (imageNumber === 1) {
      this.image1Zoom = newZoom;
      this.resetPanForZoom(newZoom, imageNumber);
    } else {
      this.image2Zoom = newZoom;
      this.resetPanForZoom(newZoom, imageNumber);
    }
  }

  private resetPanForZoom(newZoom: number, imageNumber: number): void {
    if (newZoom <= 1) {
      if (imageNumber === 1) {
        this.image1PanX = 0;
        this.image1PanY = 0;
      } else {
        this.image2PanX = 0;
        this.image2PanY = 0;
      }
    } else {
      this.constrainPan(imageNumber);
    }
  }

  resetImage(imageNumber: number): void {
    if (imageNumber === 1) {
      this.image1Zoom = 1;
      this.image1PanX = 0;
      this.image1PanY = 0;
    } else {
      this.image2Zoom = 1;
      this.image2PanX = 0;
      this.image2PanY = 0;
    }
  }

  private constrainPan(imageNumber: number): void {
    const image =
      imageNumber === 1
        ? this.image1?.nativeElement
        : this.image2?.nativeElement;
    const zoom = imageNumber === 1 ? this.image1Zoom : this.image2Zoom;

    if (!image) return;

    const container = image.parentElement;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const imageRect = image.getBoundingClientRect();

    const scaledWidth = imageRect.width * zoom;
    const scaledHeight = imageRect.height * zoom;
    const maxPanX = Math.max(0, (scaledWidth - containerRect.width) / 2);
    const maxPanY = Math.max(0, (scaledHeight - containerRect.height) / 2);

    if (imageNumber === 1) {
      this.image1PanX = Math.max(-maxPanX, Math.min(maxPanX, this.image1PanX));
      this.image1PanY = Math.max(-maxPanY, Math.min(maxPanY, this.image1PanY));
    } else {
      this.image2PanX = Math.max(-maxPanX, Math.min(maxPanX, this.image2PanX));
      this.image2PanY = Math.max(-maxPanY, Math.min(maxPanY, this.image2PanY));
    }
  }

  private extractFaceImages(): void {
    this.selectedFaces.forEach((face, index) => {
      if (!face || !this.selectedImages[index]?.url || !face.bbox) {
        return;
      }

      const faceId = `face_${index}`;
      const image = this.selectedImages[index];

      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) return;

        canvas.width = face.bbox.width;
        canvas.height = face.bbox.height;

        ctx.drawImage(
          img,
          face.bbox.x,
          face.bbox.y,
          face.bbox.width,
          face.bbox.height,
          0,
          0,
          face.bbox.width,
          face.bbox.height,
        );

        this.faceImages[faceId] = canvas.toDataURL('image/png');
      };

      img.onerror = () => {
        this.faceImages[faceId] = '';
      };

      img.src = image.url;
    });
  }
}
